import React from 'react';

import InfoIcon from 'icons/info.svg';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import DatePickerField from 'components/application/DatePickerField';
import TextArea from 'components/application/TextArea';

import { isExpiryDateAfterRenewalDate } from 'components/helpers/resources/training';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function TrainingFormFields({
  currentTraining,
  domainCourse,
  notesExplanatoryMessage,
  onInputChange,
  onTrainingCalendarClose,
  onTrainingDateChange,
  removeErrorStyling,
  requestError,
  submitDisabled,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingFormFields',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    expires: doesCourseExpires,
    renewalFrequency: courseRenewalFrequency,
    renewalInterval: courseRenewalInterval,
  } = domainCourse?.attributes || {};

  return (
    <>
      {doesCourseExpires && courseRenewalFrequency && courseRenewalInterval && (
        <div className='tw-mb-6 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
          <div className='tw-mr-3 tw-h-5'>
            <InfoIcon
              className='[&_path]:tw-fill-cyan-800'
              height={20}
              width={20}
            />
          </div>
          <p className='tw-m-0'>
            <Trans
              components={{ span: <span className='tw-font-semibold' /> }}
              i18nKey={`training.TrainingFormFields.description.${courseRenewalInterval}Interval`}
              ns='components'
              values={{ count: courseRenewalFrequency }}
            />
          </p>
        </div>
      )}
      <div className='tw-flex tw-justify-between'>
        <div className='tw-mr-3'>
          <DatePickerField
            fieldError={requestError.validationErrors.startDate}
            isRequired={true}
            label={i18nTShared('validFromDate')}
            latestDate={moment().toDate()}
            name='startDate'
            onCalendarClose={onTrainingCalendarClose}
            onChange={onTrainingDateChange}
            removeErrorStyling={removeErrorStyling}
            submitDisabled={submitDisabled}
            value={currentTraining.startDate}
          />
        </div>
        <div>
          <DatePickerField
            disabledPlaceholderText={i18nTShared('doesNotExpire')}
            earliestDate={currentTraining.startDate}
            fieldError={requestError.validationErrors.expiryDate}
            isDisabled={!doesCourseExpires}
            isRequired={doesCourseExpires}
            label={i18nTShared('expiryDate')}
            name='expiryDate'
            onCalendarClose={onTrainingCalendarClose}
            onChange={onTrainingDateChange}
            placement='bottom-end'
            removeErrorStyling={removeErrorStyling}
            submitDisabled={submitDisabled}
            value={currentTraining.expiryDate}
          />
        </div>
      </div>

      {isExpiryDateAfterRenewalDate({
        startDate: currentTraining.startDate,
        expiryDate: currentTraining.expiryDate,
        renewalFrequency: courseRenewalFrequency,
        renewalInterval: courseRenewalInterval,
      }) && (
        <div className='tw-mb-6 tw-flex tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
          <div className='tw-mr-3 tw-flex tw-h-5 tw-w-4 tw-items-center'>
            <InfoIcon
              className='[&_path]:tw-fill-amber-800'
              height={16}
              width={16}
            />
          </div>
          {i18nT('warning')}
        </div>
      )}

      <TextArea
        additionalText={notesExplanatoryMessage}
        fieldError={requestError.validationErrors.notes}
        label={i18nTShared('notes')}
        name='notes'
        onChange={onInputChange}
        value={currentTraining.notes}
      />
    </>
  );
}

TrainingFormFields.propTypes = {
  currentTraining: PropTypes.object,
  domainCourse: resourceShape('course'),
  requestError: PropTypes.object,
  onInputChange: PropTypes.func.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  submitDisabled: PropTypes.bool,
  onTrainingDateChange: PropTypes.func.isRequired,
  onTrainingCalendarClose: PropTypes.func.isRequired,
  notesExplanatoryMessage: PropTypes.string,
};
